import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  useTheme
} from '@mui/material';
import { styled } from '@mui/system';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import lightModeHeroImage from '../assets/digispark_asset_2.png';
import darkModeHeroImage from '../assets/digispark_asset_1.png';
// Removing PNG imports and using Material UI icons instead
import ComputerIcon from '@mui/icons-material/Computer';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  padding: theme.spacing(14, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(10, 0),
  },
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: theme.palette.mode === 'dark' 
      ? 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.07), transparent 70%)' 
      : 'radial-gradient(circle at top right, rgba(22, 56, 50, 0.07), transparent 70%)',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.05), transparent 70%)'
      : 'radial-gradient(circle at bottom left, rgba(22, 56, 50, 0.05), transparent 70%)',
    zIndex: 0,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));


const Home = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  
  // Extract language from the current URL path
  const match = location.pathname.match(/^\/(en|fr)(\/|$)/);
  const currentLang = match ? match[1] : 'en';
  
  // Function to generate language-aware links
  const getLanguagePath = (path) => {
    return `/${currentLang}${path}`;
  };
  
  // Determine which hero image to use based on theme mode
  const heroImage = theme.palette.mode === 'dark' ? darkModeHeroImage : lightModeHeroImage;

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ 
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)'
                  }
                }
              }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800,
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    backgroundImage: theme.palette.mode === 'dark'
                      ? 'linear-gradient(90deg, #fff 0%, #B0BEC5 100%)'
                      : 'linear-gradient(90deg, #2C3E50 0%, #163832 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    ...(theme.palette.mode === 'dark' && { WebkitTextFillColor: 'transparent' })
                  }}
                >
                  {t('home.heroTitle')}
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4,
                    color: 'text.secondary',
                    fontWeight: 500,
                    animation: 'fadeInUp 0.8s ease-out 0.2s both',
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(20px)'
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    }
                  }}
                >
                  {t('home.heroSubtitle')}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexWrap: 'wrap',
                  animation: 'fadeInUp 0.8s ease-out 0.4s both',
                  '@keyframes fadeInUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(20px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)'
                    }
                  }
                }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large" 
                    component={Link}
                    to={getLanguagePath('/programs')}
                    sx={{ 
                      fontWeight: 700,
                      py: 1.5, 
                      px: 4
                    }}
                  >
                    {t('home.ourPrograms')}
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="large" 
                    component={Link}
                    to={getLanguagePath('/contact')}
                    sx={{ 
                      fontWeight: 700,
                      py: 1.5, 
                      px: 4,
                      borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                      color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : theme.palette.primary.dark,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(22, 56, 50, 0.04)',
                      }
                    }}
                  >
                    {t('home.getInvolved')}
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={heroImage}
                alt="Digital Future"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark' 
                    ? '0 20px 40px rgba(0, 0, 0, 0.3)' 
                    : '0 20px 40px rgba(22, 56, 50, 0.15)',
                  transform: 'perspective(1000px) rotateY(-5deg)',
                  transition: 'transform 0.6s ease-out',
                  animation: 'fadeIn 1s ease-out',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'perspective(1000px) rotateY(-5deg) translateX(30px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'perspective(1000px) rotateY(-5deg) translateX(0)'
                    }
                  },
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(0deg)'
                  },
                  position: 'relative',
                  zIndex: 1
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Mission Section */}
      <Box sx={{ 
        py: 10, 
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark' 
            ? 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)' 
            : 'radial-gradient(circle, rgba(22, 56, 50, 0.05) 0%, transparent 70%)',
          zIndex: 0,
        }
      }}>
        <Container maxWidth="md">
          <Box sx={{ 
            textAlign: 'center', 
            mb: 6,
            position: 'relative',
            zIndex: 1
          }}>
            <SectionTitle variant="h3" align="center" sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(22, 56, 50, 0.07)',
                top: '-15px',
                left: 'calc(50% - 90px)',
                zIndex: -1,
              }
            }}>
              {t('home.missionTitle')}
            </SectionTitle>
            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('home.missionSubtitle')}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ 
            fontSize: '1.1rem', 
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: 1.8
          }}>
            {t('home.missionDescription')}
          </Typography>
        </Container>
      </Box>

      {/* Programs Section - Updated with better visual representations */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <SectionTitle variant="h3" align="center">
              {t('home.keyInitiativesTitle')}
            </SectionTitle>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('home.keyInitiativesSubtitle')}
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                },
              }}>
                <Box 
                  sx={{ 
                    height: '120px',
                    backgroundColor: theme.palette.mode === 'dark' ? '#1B1C1D' : '#263238',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#fff',
                    padding: theme.spacing(2.5),
                    textAlign: 'center',
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                  }}
                >
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('home.techEducationTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {t('home.techEducationSubtitle')}
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography>
                    {t('home.techEducationDescription')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2 }}>
                  <Button 
                    size="medium" 
                    component={Link} 
                    to={getLanguagePath('/programs#tech-education')}
                    color="primary"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#ffffff' : undefined,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : undefined,
                      }
                    }}
                  >
                    {t('home.learnMore')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                },
              }}>
                <Box 
                  sx={{ 
                    height: '120px',
                    backgroundColor: "#163832",
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#fff',
                    padding: theme.spacing(2.5),
                    textAlign: 'center',
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                  }}
                >
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('home.agriculturalTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {t('home.agriculturalSubtitle')}
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography>
                    {t('home.agriculturalDescription')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2 }}>
                  <Button 
                    size="medium" 
                    component={Link} 
                    to={getLanguagePath('/programs#agricultural')}
                    color="primary"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#ffffff' : undefined,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : undefined,
                      }
                    }}
                  >
                    {t('home.learnMore')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                },
              }}>
                <Box 
                  sx={{ 
                    height: '120px',
                    backgroundColor: "#3949AB",
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#fff',
                    padding: theme.spacing(2.5),
                    textAlign: 'center',
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                  }}
                >
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('home.healthcareTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {t('home.healthcareSubtitle')}
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography>
                    {t('home.healthcareDescription')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2 }}>
                  <Button 
                    size="medium" 
                    component={Link} 
                    to={getLanguagePath('/programs#healthcare')}
                    color="primary"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#ffffff' : undefined,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : undefined,
                      }
                    }}
                  >
                    {t('home.learnMore')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: 12, 
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="rgba(255,255,255,.05)" fill-rule="evenodd"/%3E%3C/svg%3E")',
            backgroundSize: '15px',
            zIndex: 0,
            opacity: 0.5,
          },
          color: '#fff'
        }}
      >
        <Container maxWidth="md" sx={{ 
          position: 'relative',
          zIndex: 1,
        }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ position: 'relative' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800, 
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    display: 'block',
                    width: '80px',
                    height: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    marginTop: theme.spacing(1.5),
                  }
                }}>
                  {t('home.callToActionTitle')}
                </Typography>
                <Typography variant="body1" sx={{ 
                  mb: 5, 
                  fontSize: '1.2rem',
                  maxWidth: '700px',
                  lineHeight: 1.6,
                  opacity: 0.9
                }}>
                  {t('home.callToActionDescription')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                component={Link}
                to={getLanguagePath('/contact')}
                sx={{ 
                  fontWeight: 800,
                  py: 2, 
                  px: 5,
                  fontSize: '1.1rem',
                  letterSpacing: '0.5px',
                  borderRadius: 2,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  border: '2px solid rgba(0,0,0,0.8)',
                  textTransform: 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease',
                  },
                  '&:hover': {
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                    transform: 'translateY(-3px) scale(1.03)',
                    backgroundColor: '#222222',
                    '&::before': {
                      transform: 'translateX(100%)',
                    }
                  }
                }}
              >
                {t('home.getInvolvedToday')}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default Home; 